// yogaNidraScan.ts
// The Yoga Nidra body scan: one detailed pass, feet → crown, ending with the
// whole body. Shared by 11_yogaNidra.tsx (tutorial) and 11_yogaNidra_body.tsx
// (after login).
//
// Each point plays /audio/nidra-<name, lowercase, no spaces>.mp3 and lights up
// a region of the body figure: `svgKey` is the figure part, `clipY` the band
// of it (in that part's own SVG units, null = the whole part). "*" lights the
// whole figure. `name` is the English identifier (also the audio file name —
// never translate it); `de` is the German name shown on screen.
//
// Each clip says "Inhale <part>. Exhale. Release. Relax." `exhaleAt` is when
// "Exhale" starts in that clip (seconds, measured from the ENGLISH recordings):
// the breath circle switches from INHALE to EXHALE there. Re-measure after
// re-recording a clip. When a German recording is playing, `nidraExhaleAt`
// falls back to 40 % of the clip instead (German timings not measured yet).
import { ByLang, getLang, pick } from "i18n/lang";
import { hasLocalAudio } from "i18n/audio";

export interface NidraStep {
  name: string;
  de: string;
  svgKey: string;
  clipY: [number, number] | null;
  exhaleAt: number;
}

// Used when a clip is missing or fails to load: the scan waits this long on
// the point, then moves on instead of stalling.
export const NIDRA_FALLBACK_SEC = 6.8;

export const NIDRA_STEPS: NidraStep[] = [
  // Feet & legs
  { name: "Toes",              de: "Zehen",            svgKey: "legs",     clipY: [272, 287], exhaleAt: 2.60 },
  { name: "Soles",             de: "Fußsohlen",        svgKey: "legs",     clipY: [262, 287], exhaleAt: 3.30 },
  { name: "Heels",             de: "Fersen",           svgKey: "legs",     clipY: [252, 272], exhaleAt: 2.45 },
  { name: "Feet",              de: "Füße",             svgKey: "legs",     clipY: [248, 287], exhaleAt: 2.15 },
  { name: "Ankles",            de: "Knöchel",          svgKey: "legs",     clipY: [224, 262], exhaleAt: 2.60 },
  { name: "Shins",             de: "Schienbeine",      svgKey: "legs",     clipY: [160, 228], exhaleAt: 2.65 },
  { name: "Calves",            de: "Waden",            svgKey: "legs",     clipY: [152, 228], exhaleAt: 2.70 },
  { name: "Knees",             de: "Knie",             svgKey: "legs",     clipY: [118, 158], exhaleAt: 2.65 },
  { name: "Backs of the Knees",de: "Kniekehlen",       svgKey: "legs",     clipY: [126, 152], exhaleAt: 3.25 },
  { name: "Thighs",            de: "Oberschenkel",     svgKey: "legs",     clipY: [0,   122], exhaleAt: 2.70 },
  // Hips, back & torso
  { name: "Hips",              de: "Hüften",           svgKey: "stomach",  clipY: [56,  108], exhaleAt: 2.50 },
  { name: "Buttocks",          de: "Gesäß",            svgKey: "stomach",  clipY: [72,  108], exhaleAt: 2.60 },
  { name: "Lower Back",        de: "Unterer Rücken",   svgKey: "stomach",  clipY: [40,  90],  exhaleAt: 2.90 },
  { name: "Upper Back",        de: "Oberer Rücken",    svgKey: "chest",    clipY: null,       exhaleAt: 2.75 },
  { name: "Shoulder Blades",   de: "Schulterblätter",  svgKey: "shoulder", clipY: [14,  47],  exhaleAt: 2.85 },
  { name: "Abdomen",           de: "Bauch",            svgKey: "stomach",  clipY: [0,   60],  exhaleAt: 2.35 },
  { name: "Navel",             de: "Nabel",            svgKey: "stomach",  clipY: [36,  60],  exhaleAt: 2.35 },
  { name: "Ribs",              de: "Rippen",           svgKey: "stomach",  clipY: [0,   36],  exhaleAt: 2.35 },
  { name: "Chest",             de: "Brust",            svgKey: "chest",    clipY: null,       exhaleAt: 2.65 },
  { name: "Collarbones",       de: "Schlüsselbeine",   svgKey: "shoulder", clipY: [0,   14],  exhaleAt: 2.60 },
  // Hands & arms
  { name: "Fingertips",        de: "Fingerspitzen",    svgKey: "hands",    clipY: [30,  40],  exhaleAt: 2.65 },
  { name: "Fingers",           de: "Finger",           svgKey: "hands",    clipY: [16,  40],  exhaleAt: 2.65 },
  { name: "Palms",             de: "Handflächen",      svgKey: "hands",    clipY: null,       exhaleAt: 2.55 },
  { name: "Backs of the Hands",de: "Handrücken",       svgKey: "hands",    clipY: [0,   22],  exhaleAt: 3.25 },
  { name: "Wrists",            de: "Handgelenke",      svgKey: "arm",      clipY: [100, 120], exhaleAt: 2.65 },
  { name: "Forearms",          de: "Unterarme",        svgKey: "arm",      clipY: [52,  119], exhaleAt: 2.90 },
  { name: "Elbows",            de: "Ellbogen",         svgKey: "arm",      clipY: [40,  64],  exhaleAt: 3.10 },
  { name: "Upper Arms",        de: "Oberarme",         svgKey: "arm",      clipY: [-30, 58],  exhaleAt: 2.90 },
  { name: "Shoulders",         de: "Schultern",        svgKey: "shoulder", clipY: null,       exhaleAt: 3.00 },
  // Neck & head — head units: hair 0–38, face 14–75 (ears 40–52), neck 68–95
  { name: "Neck",              de: "Nacken",           svgKey: "head",     clipY: [68,  95],  exhaleAt: 2.35 },
  { name: "Throat",            de: "Kehle",            svgKey: "head",     clipY: [76,  95],  exhaleAt: 2.65 },
  { name: "Chin",              de: "Kinn",             svgKey: "head",     clipY: [66,  76],  exhaleAt: 2.45 },
  { name: "Jaw",               de: "Kiefer",           svgKey: "head",     clipY: [60,  76],  exhaleAt: 2.60 },
  { name: "Lips",              de: "Lippen",           svgKey: "head",     clipY: [57,  65],  exhaleAt: 2.50 },
  { name: "Tongue",            de: "Zunge",            svgKey: "head",     clipY: [56,  65],  exhaleAt: 2.35 },
  { name: "Cheeks",            de: "Wangen",           svgKey: "head",     clipY: [46,  61],  exhaleAt: 2.50 },
  { name: "Nose",              de: "Nase",             svgKey: "head",     clipY: [42,  57],  exhaleAt: 2.35 },
  { name: "Ears",              de: "Ohren",            svgKey: "head",     clipY: [39,  53],  exhaleAt: 2.55 },
  { name: "Eyes",              de: "Augen",            svgKey: "head",     clipY: [37,  45],  exhaleAt: 2.30 },
  { name: "Eyebrows",          de: "Augenbrauen",      svgKey: "head",     clipY: [32,  38],  exhaleAt: 2.55 },
  { name: "Eyebrow Centre",    de: "Augenbrauenmitte", svgKey: "head",     clipY: [33,  40],  exhaleAt: 3.90 },
  { name: "Forehead",          de: "Stirn",            svgKey: "head",     clipY: [20,  35],  exhaleAt: 2.85 },
  { name: "Temples",           de: "Schläfen",         svgKey: "head",     clipY: [28,  43],  exhaleAt: 2.45 },
  { name: "Back of the Head",  de: "Hinterkopf",       svgKey: "head",     clipY: [0,   40],  exhaleAt: 3.30 },
  { name: "Crown",             de: "Scheitel",         svgKey: "head",     clipY: [0,   18],  exhaleAt: 2.75 },
  // Close
  { name: "Whole Body",        de: "Ganzer Körper",    svgKey: "*",        clipY: null,       exhaleAt: 3.60 },
];

export const nidraAudioFile = (name: string) =>
  `/audio/nidra-${name.toLowerCase().replace(/\s+/g, "")}.mp3`;

/** On-screen name of a scan point in the current language. */
export const nidraStepLabel = (s: NidraStep): string =>
  pick<string>({ en: s.name, de: s.de });

/**
 * Seconds into the clip where "Exhale" starts. `exhaleAt` was measured on the
 * English recordings, so it is used only while the English clip plays; a
 * German clip uses the 40 % estimate.
 */
export const nidraExhaleAt = (s: NidraStep, dur: number): number => {
  const englishClip = getLang() === "en" || !hasLocalAudio(nidraAudioFile(s.name));
  return englishClip && s.exhaleAt < dur ? s.exhaleAt : dur * 0.40;
};

// Summary line for the "Body Awareness" step card.
export const NIDRA_SCAN_SUMMARY: ByLang<string> = {
  en: "Feet · Legs · Hips & Back · Torso · Hands & Arms · Neck · Face · Crown · Whole Body",
  de: "Füße · Beine · Hüften & Rücken · Rumpf · Hände & Arme · Nacken · Gesicht · Scheitel · ganzer Körper",
};
